import { useState, useRef, useCallback } from 'react';
import { openDropboxUpload } from '../../../components/feature/DropboxUploadModal';
import { useAuth } from '../../../hooks/useAuth';

function HeroSlider() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging.current) handleMove(e.clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-3xl mx-auto h-[340px] sm:h-[400px] md:h-[440px] rounded-2xl overflow-hidden cursor-ew-resize select-none shadow-2xl ring-1 ring-black/5"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
    >
      {/* Before Image (Background - full) */}
      <img
        src="https://static.readdy.ai/image/fcb9caa8ea4da54b71dc0a368af215b0/900064e020bd981b039231f12b8cad76.jpeg"
        alt="Before virtual staging - empty living room"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />

      {/* After Image (Clipped from left) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src="https://static.readdy.ai/image/fcb9caa8ea4da54b71dc0a368af215b0/265f53ece385ede4f80d8a38e4a45d37.jpeg"
          alt="After virtual staging - furnished living room"
          className="absolute inset-0 h-full object-cover object-top"
          style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%', maxWidth: 'none' }}
        />
      </div>

      {/* Slider Line */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white/90 z-10 pointer-events-none"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-white rounded-full shadow-xl flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-0">
            <i className="ri-arrow-left-s-line text-gray-800 text-base"></i>
            <i className="ri-arrow-right-s-line text-gray-800 text-base"></i>
          </div>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase pointer-events-none">
        Before
      </div>
      <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase pointer-events-none">
        After
      </div>
    </div>
  );
}

export default function Hero() {
  const { user } = useAuth();
  
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleQuickUpload = () => {
    openDropboxUpload();
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/1716b7fb3570dc45c2dc329d968de5ef.jpg"
          alt="Home background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/65 via-white/60 to-white/65"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-col items-center gap-12">
          <div className="space-y-8 text-center max-w-2xl">
            <div className="space-y-4">
              <p className="text-xs font-light tracking-widest text-[#FF6B6B] uppercase">
                Transform Empty Spaces
              </p>
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold leading-tight">
                AI Virtual Staging
                <span className="block italic font-serif font-light text-4xl sm:text-5xl md:text-6xl mt-2">
                  That Sells
                </span>
                <span className="block font-bold">Faster</span>
              </h1>
              <p className="text-lg text-gray-600 font-light">
                Professional 3D furniture placement in 24 hours
              </p>
            </div>

            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E8E] text-white text-sm font-medium rounded-full hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get Started
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="px-8 py-4 bg-black text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                View Pricing
              </button>
              {user && (
                <button
                  onClick={handleQuickUpload}
                  className="px-8 py-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E8E] text-white text-sm font-medium rounded-full hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer flex items-center gap-2.5"
                >
                  <i className="ri-upload-cloud-2-line text-lg"></i>
                  Quick Upload
                </button>
              )}
            </div>
          </div>

          {/* Before/After Slider */}
          <div className="w-full">
            <HeroSlider />
            <p className="text-center text-sm text-gray-500 mt-4 font-light">
              <i className="ri-drag-move-2-line mr-1"></i>
              Drag the slider to see the transformation
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
